import { useCallback, useMemo, useState } from "react";
import type { LvMatch } from "../../utils/types";
import type { UseLvMatchEditorArgs, UseLvMatchEditorResult } from "./types";

const MANUAL_OVERRIDE_CONFIDENCE = "high";
const MANUAL_OVERRIDE_REASONING = "";

export const NO_LV_POSITION = "__no_lv_position__";

export const useLvMatchEditor = ({
  lvMatch,
  boqPositions,
  saveConfirmation = "required",
  onSave,
}: UseLvMatchEditorArgs): UseLvMatchEditorResult => {
  const [isEditing, setIsEditing] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [pendingPositionNumber, setPendingPositionNumber] = useState<
    string | null
  >(null);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const filteredPositions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return boqPositions;
    return boqPositions.filter((position) => {
      const haystack = `${position.position_number} ${position.description} ${position.section_name}`;
      return haystack.toLowerCase().includes(query);
    });
  }, [boqPositions, searchQuery]);

  const originalSelection = lvMatch?.lvId ?? NO_LV_POSITION;
  const isRemoving = pendingPositionNumber === NO_LV_POSITION;

  const canSave =
    pendingPositionNumber !== null &&
    pendingPositionNumber !== originalSelection &&
    !isSaving;

  const openEditor = useCallback(() => {
    setIsEditing(true);
    setSearchQuery("");
    setPendingPositionNumber(lvMatch?.lvId ?? NO_LV_POSITION);
    setErrorMessage(null);
  }, [lvMatch?.lvId]);

  const closeEditor = useCallback(() => {
    setIsEditing(false);
    setIsConfirmOpen(false);
    setSearchQuery("");
    setPendingPositionNumber(null);
    setErrorMessage(null);
  }, []);

  const selectPosition = useCallback((positionNumber: string) => {
    setPendingPositionNumber(positionNumber);
    setErrorMessage(null);
  }, []);

  const closeSaveConfirmation = useCallback(() => {
    setIsConfirmOpen(false);
  }, []);

  const confirmSave = useCallback(async () => {
    if (!pendingPositionNumber) return;

    let nextLvMatch: LvMatch;
    if (pendingPositionNumber === NO_LV_POSITION) {
      // Empty match clears lvMatchId/Name/Confidence/Reasoning across the group
      nextLvMatch = {};
    } else {
      const position = boqPositions.find(
        (item) => item.position_number === pendingPositionNumber,
      );
      if (!position) return;

      // Match the agent convention: lvName is the LV Kurztext (first line), not the full Langtext
      const lvName =
        position.description.split("\n")[0] || position.description;

      nextLvMatch = {
        lvId: position.position_number,
        lvName,
        confidence: MANUAL_OVERRIDE_CONFIDENCE,
        reasoning: MANUAL_OVERRIDE_REASONING,
      };
    }

    setIsSaving(true);
    setErrorMessage(null);
    try {
      await onSave(nextLvMatch);
      setIsEditing(false);
      setSearchQuery("");
      setPendingPositionNumber(null);
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Failed to save BOQ position",
      );
    } finally {
      setIsSaving(false);
    }
  }, [boqPositions, onSave, pendingPositionNumber]);

  const openSaveConfirmation = useCallback(async () => {
    if (!pendingPositionNumber || pendingPositionNumber === originalSelection) {
      return;
    }
    if (saveConfirmation === "none") {
      await confirmSave();
      return;
    }
    setIsConfirmOpen(true);
  }, [pendingPositionNumber, originalSelection, saveConfirmation, confirmSave]);

  return {
    isEditing,
    isConfirmOpen,
    searchQuery,
    filteredPositions,
    pendingPositionNumber,
    isSaving,
    errorMessage,
    canSave,
    isRemoving,
    openEditor,
    closeEditor,
    setSearchQuery,
    selectPosition,
    openSaveConfirmation,
    closeSaveConfirmation,
    confirmSave,
  };
};
